import * as React from 'react'
import { Product, ProductColumnConfig, ProductColumnProperty, ProductColumns, ProductProperty, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import showdown from '../../utilities/showdown'

export const DEFAULT_PRODUCT_COLUMNS: ProductColumnProperty[] = [
  'name',
  'supplier',
  'physical',
  'stock',
  'price',
]

const ProductColumnConfigs: Record<ProductColumnProperty, ProductColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('Product::id') }),
    render: ({ product }) => product.id || '-'
  },
  name: {
    header: () => ({ title: i18n.t('Product::name') }),
    render: ({ product }) => product.name || '-',
  },
  supplier: {
    header: () => ({ title: i18n.t('Product::supplier') }),
    render: ({ product }) => product.supplier?.name || '-'
  },
  notes: {
    header: () => ({ title: i18n.t('Product::notes') }),
    render: ({ product }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(product.notes) }} />
  },
  description: {
    header: () => ({ title: i18n.t('Product::description') }),
    render: ({ product }) => product.description || '-'
  },
  purchase_price: {
    header: () => ({ title: i18n.t('Product::purchase_price') }),
    render: ({ product, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, product.purchase_price)
  },
  price: {
    header: () => ({ title: i18n.t('Product::price') }),
    render: ({ product, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, product.price)
  },
  gross_price: {
    header: () => ({ title: i18n.t('Product::gross_price') }),
    render: ({ product, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, product.gross_price)
  },
  tax_rate: {
    header: () => ({ title: i18n.t('Product::tax_rate') }),
    render: ({ product, setting }) => product.tax_rate ? `${NumberFormatter.formatNumber(setting.number_format, Number(product.tax_rate) * 100)}%` : '-'
  },
  tax_code: {
    header: () => ({ title: i18n.t('Product::tax_code') }),
    render: ({ product }) => product.tax_code || '-'
  },
  stock: {
    header: () => ({ title: i18n.t('Product::stock') }),
    render: ({ product, setting }) => product.physical ? product.stock : i18n.t('Product::N/A')
  },
  minimum_stock: {
    header: () => ({ title: i18n.t('Product::minimum_stock') }),
    render: ({ product, setting }) => product.minimum_stock
  },
  sku: {
    header: () => ({ title: i18n.t('Product::sku') }),
    render: ({ product }) => product.sku || '-'
  },
  barcode: {
    header: () => ({ title: i18n.t('Product::barcode') }),
    render: ({ product }) => product.barcode || '-'
  },
  physical: {
    header: () => ({ title: i18n.t('Product::physical') }),
    render: ({ product }) => product.physical ? i18n.t('Product::Yes') : i18n.t('Product::No')
  },
  created_at: {
    header: () => ({ title: i18n.t('Product::created_at') }),
    render: ({ product, setting }) => product.created_at ? moment(product.created_at).format(setting.date_format) : '-'
  },
}

export function getProductTableRenderer(options: ResourceRendererOptions<Product>): ResourceRendererResult<Product, ProductProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = ProductColumns.map(column => ({ label: i18n.t(`Product::${column}`), value: column }))
  const selectedColumns: ProductProperty[] = LocalStorage
    .get(LocalStorageKey.PRODUCT_COLUMNS, DEFAULT_PRODUCT_COLUMNS)
    .filter((column: ProductColumnProperty) => ProductColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...ProductColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (product: Product, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(product.id)
    let actions: IActionListItem[] = [
      { key: 'edit', icon: 'edit-solid', content: i18n.t('ProductsTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('ProductsTable::Delete'), destructive: true }
    ]

    const applyMaxWidthColumns: ProductProperty[] = [
      'name',
      'notes',
      'description',
      'supplier'
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${product.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(product) : undefined}
        maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
        ellipse={applyMaxWidthColumns.includes(column)}
        textAlign={ProductColumnConfigs[column].header().align}
      >
        {ProductColumnConfigs[column].render({ product, ...options })}
      </ResourceTableRowData>
    ))

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${product.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, product)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={product.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, product.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getProductTableRenderer
